import {useState} from 'react';
import {
  TextConversationMessage,
  ConversationMessageStatus,
  Conversation,
} from '@stripe-internal/conversations-client';
import {FormattedMessage} from 'react-intl';
import {view, css, Link} from '@sail/ui/deprecated';
import {messages} from './messages';
import {TextRow} from '../../../Chat';
import {AvatarSpacer} from '../../../Chat/AvatarSpacer';
import {useTheme} from '../../../ThemeProvider';
import {useNewUniversalHelpDesign} from '../../../FeatureFlagsProvider';

type Props = {
  conversation: Conversation;
  message: TextConversationMessage;
  children?: React.ReactNode;
  avatar?: React.ReactNode | null | undefined;
};

export const TextMessageRow = ({
  conversation,
  message,
  avatar,
  children,
}: Props) => {
  const useNewDesign = useNewUniversalHelpDesign();
  const theme = useTheme();
  const [showOriginalMessage, setShowOriginalMessage] = useState(false);

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const isOwnMessage = message.participant.id === conversation.user.id;
  const isBot = message.participant.role === 'bot';
  const position = isOwnMessage ? 'end' : 'start';

  const showMessageClickHandler = () => {
    setShowOriginalMessage(!showOriginalMessage);
  };

  const hasOriginalMessage =
    message.status !== ConversationMessageStatus.Error &&
    message.originalText &&
    message.originalText !== message.text &&
    !isOwnMessage;

  let messageStyle = message.style;
  if (useNewDesign) {
    // The new designs remove the bubble for bot answers. We still
    // render the `info` style if it's used
    messageStyle = message.style !== 'info' && isBot ? 'none' : message.style;
  }

  return (
    <view.div
      className="uh-conversations-text-message-row"
      data-testid="text-message-row"
      data-message-type={message.participant.role}
    >
      <TextRow
        body={
          showOriginalMessage && message.originalText
            ? message.originalText
            : message.text
        }
        messageType={message.participant.role}
        avatar={avatar}
        position={position}
        background={background}
        error={
          message.status === ConversationMessageStatus.Error ? (
            <FormattedMessage {...messages.sendError} />
          ) : null
        }
        style={messageStyle}
      >
        {children}
      </TextRow>
      {hasOriginalMessage ? (
        <view.div
          css={{
            paddingTop: 'small',
            paddingBottom: 'small',
            stack: 'x',
            justifyContent:
              position === 'end'
                ? css.value('flex-end')
                : css.value('flex-start'),
            gap: 'small',
          }}
        >
          {!useNewDesign && avatar ? <AvatarSpacer /> : null}
          <Link
            type="primary"
            onPress={showMessageClickHandler}
            css={{
              color: css.value(theme.linkColor),
              '&:focus': {
                outline: theme.focusVisibleOutline,
                focusRing: theme.focusVisibleOutline
                  ? css.value('none')
                  : undefined,
              },
            }}
          >
            {showOriginalMessage ? (
              <FormattedMessage {...messages.translateCta} />
            ) : (
              <FormattedMessage {...messages.viewOriginalCta} />
            )}
          </Link>
        </view.div>
      ) : null}
    </view.div>
  );
};
